import React from 'react';
import { Box, Button, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { NewsCluster } from '../../types/types';
import ClusterRadarDetailPanel from '../ClusterRadarDetailPanel';

export type LibraryClusterInsightsDrawerProps = {
  open: boolean;
  cluster: NewsCluster | null;
  onClose: () => void;
  /** When opening from the article library drawer, show a control to return to the draft. */
  showBackToArticle?: boolean;
  onBackToArticle?: () => void;
  /** Label for the back control (e.g. "Back to podcast"). Defaults to "Back to article". */
  draftBackLabel?: string;
  incorrectArticles?: string[];
  onArticleFeedback?: (clusterId: string, articleUrl: string) => Promise<void>;
  isGeneratingContent?: boolean;
};

const DRAWER_WIDTH = '70vw';

const LibraryClusterInsightsDrawer: React.FC<LibraryClusterInsightsDrawerProps> = ({
  open,
  cluster,
  onClose,
  showBackToArticle,
  onBackToArticle,
  draftBackLabel = 'Back to article',
  incorrectArticles = [],
  onArticleFeedback,
  isGeneratingContent = false,
}) => {
  return (
    <Drawer
      anchor="right"
      open={open && Boolean(cluster)}
      onClose={onClose}
      ModalProps={{
        keepMounted: false,
        disableEnforceFocus: true,
        disableAutoFocus: true,
        sx: { zIndex: (theme) => theme.zIndex.drawer + 3 },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: { xs: '100%', sm: 1600 },
          bgcolor: '#0a0a0a',
          borderLeft: '1px solid #27272a',
          display: 'flex',
          flexDirection: 'column',
          zIndex: (theme) => theme.zIndex.drawer + 3,
        },
      }}
    >
      {!cluster ? null : (
        <>
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.25,
              borderBottom: '1px solid #27272a',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" sx={{ color: '#71717a', textTransform: 'uppercase', letterSpacing: 0.06 }}>
                Cluster
              </Typography>
              <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 700, mt: 0.25 }}>
                {cluster.title}
              </Typography>
              {isGeneratingContent ? (
                <Typography variant="caption" sx={{ color: '#60a5fa', display: 'block', mt: 0.5 }}>
                  Generating draft content…
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              {showBackToArticle && onBackToArticle ? (
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                  onClick={onBackToArticle}
                  sx={{ color: '#e4e4e7', textTransform: 'none', mr: 0.5 }}
                >
                  {draftBackLabel}
                </Button>
              ) : null}
              <IconButton onClick={onClose} aria-label="Close cluster details" sx={{ color: '#a1a1aa', '&:hover': { color: '#f87171' } }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <ClusterRadarDetailPanel
              cluster={cluster}
              onArticleFeedback={onArticleFeedback}
              incorrectArticles={incorrectArticles}
              layoutVariant="library"
            />
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default LibraryClusterInsightsDrawer;
